/**
 * Feedback components: `MuiAlert`, `MuiTooltip`, `MuiSkeleton` — alert severities, tooltip chrome, skeleton pulse.
 * Discoverability: `MuiAlert`, `MuiTooltip`, `MuiSkeleton`, `Alert`, `Tooltip`.
 *
 * @see https://mui.com/material-ui/api/alert/
 * @see https://mui.com/material-ui/api/tooltip/
 * @see https://mui.com/material-ui/api/skeleton/
 *
 */
import type { Components, Theme } from '@mui/material/styles';

export function muiFeedback(theme: Theme): Pick<Components<Theme>, 'MuiAlert' | 'MuiTooltip' | 'MuiSkeleton'> {
  return {
    MuiAlert: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1, 2),
        },
        standardSuccess: {
          color: theme.palette.success.dark,
          backgroundColor: theme.palette.success.light,
        },
        standardInfo: {
          color: theme.palette.info.dark,
          backgroundColor: theme.palette.info.light,
        },
        standardWarning: {
          color: theme.palette.warning.dark,
          backgroundColor: theme.palette.warning.light,
        },
        standardError: {
          color: theme.palette.error.dark,
          backgroundColor: theme.palette.error.light,
        },
        filledSuccess: {
          color: theme.palette.success.contrastText,
          backgroundColor: theme.palette.success.main,
        },
        filledInfo: {
          color: theme.palette.info.contrastText,
          backgroundColor: theme.palette.info.main,
        },
        filledWarning: {
          color: theme.palette.warning.contrastText,
          backgroundColor: theme.palette.warning.main,
        },
        filledError: {
          color: theme.palette.error.contrastText,
          backgroundColor: theme.palette.error.main,
        },
        outlinedSuccess: {
          color: theme.palette.success.dark,
          borderColor: theme.palette.success.main,
        },
        outlinedInfo: {
          color: theme.palette.info.dark,
          borderColor: theme.palette.info.main,
        },
        outlinedWarning: {
          color: theme.palette.warning.dark,
          borderColor: theme.palette.warning.main,
        },
        outlinedError: {
          color: theme.palette.error.dark,
          borderColor: theme.palette.error.main,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: theme.palette.text.primary,
          color: theme.palette.background.paper,
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(0.5),
          padding: theme.spacing(1),
          ...theme.typography.paragraphRegular,
        },
        arrow: {
          color: theme.palette.text.primary,
        },
      },
    },
    MuiSkeleton: {
      defaultProps: {
        animation: 'pulse',
      },
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius,
          backgroundColor: theme.palette.skeleton.light,
        },
        circular: {
          borderRadius: '50%',
        },
      },
    },
  };
}
